import { Description } from '@/Description/Description';
import { ErrorMessage } from '@/ErrorMessage/ErrorMessage';
import { Label } from '@/Label/Label';

import type { CheckboxGroupProps as ReactAriaCheckboxGroupProps } from 'react-aria-components';
import { CheckboxGroup as AriaCheckboxGroup } from 'react-aria-components';

import { mergeClassNames } from '@/utils/mergeClassNames';
import styles from './CheckboxGroup.module.scss';

export type CheckboxGroupProps = ReactAriaCheckboxGroupProps & {
  /** Text rendered underneath the `Checkbox` options to provide additional context */
  description?: string;
  /** An accessible label to associate with the `CheckboxGroup`. If not provided, `aria-label` or `aria-labelledby` must be provided. */
  label?: string;
  /** A red error message to display below the description if this form element is "invalid". */
  errorMessage?: string;
};

/**
 * `CheckboxGroup` groups a number of `Checkbox` components, allowing them to be treated as one form element.
 *
 * ```ts
 * <CheckboxGroup
 *   description="Description"
 *   errorMessage="Errored"
 *   label="Checkbox group label"
 *   defaultValue={['option1', 'option3']}
 * >
 *   <Checkbox value="option1">Option 1</Checkbox>
 *   <Checkbox value="option2">Option 2</Checkbox>
 *   <Checkbox value="option3">Option 3</Checkbox>
 *   <Checkbox value="option4">Option 4</Checkbox>
 * </CheckboxGroup>
 * ```
 */
export const CheckboxGroup = ({
  children,
  className,
  label,
  description,
  errorMessage,
  isRequired,
  ...props
}: CheckboxGroupProps) => {
  return (
    <AriaCheckboxGroup
      className={(renderProps) =>
        mergeClassNames([styles.checkboxGroup, className], renderProps)
      }
      data-zds
      {...props}
    >
      {(checkboxGroupRenderProps) => (
        <>
          {label && <Label isRequired={isRequired}>{label}</Label>}
          <div className={styles.optionsWrapper}>
            {typeof children === 'function'
              ? children(checkboxGroupRenderProps)
              : children}
          </div>
          {description && <Description>{description}</Description>}
          <ErrorMessage>{errorMessage}</ErrorMessage>
        </>
      )}
    </AriaCheckboxGroup>
  );
};
